import React, { useEffect, useState, useRef } from 'react';
import { X, CheckCircle2, AlertCircle } from 'lucide-react';
import '../styles/ToastPortal.css';

const MOBILE_BREAKPOINT = 640; // px

const ToastPortal: React.FC = () => {
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'celebration';
    visible: boolean;
  }>({
    message: '',
    type: 'success',
    visible: false,
  });

  const [isMobile, setIsMobile] = useState<boolean>(
    () => window.innerWidth < MOBILE_BREAKPOINT
  );
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = (e: any) => {
      setToast({ message: e.detail.message, type: e.detail.type, visible: true });
      const duration =
        e.detail.type === 'error'
          ? isMobile ? 3500 : 6000
          : e.detail.type === 'celebration'
          ? 3000
          : isMobile ? 2000 : 3000;

      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setToast(t => ({ ...t, visible: false })), duration);
    };

    window.addEventListener('global-toast', handler);
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('global-toast', handler);
      window.removeEventListener('resize', onResize);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [isMobile]);

  const toastRef = useRef<HTMLDivElement | null>(null);
  const touchStartY = useRef<number | null>(null);
  const [swipeOpacity, setSwipeOpacity] = useState(1);

  // Swipe to dismiss with fade feedback
  useEffect(() => {
    if (!isMobile || !toastRef.current) return;
    const el = toastRef.current;

    const onTouchStart = (ev: TouchEvent) => {
      touchStartY.current = ev.touches[0].clientY;
    };

    const onTouchMove = (ev: TouchEvent) => {
      if (touchStartY.current == null) return;
      const dy = ev.touches[0].clientY - touchStartY.current;
      setSwipeOpacity(Math.max(0.3, 1 - dy / 100));
      if (dy > 60) setToast(t => ({ ...t, visible: false }));
    };

    const onTouchEnd = () => {
      setSwipeOpacity(1);
      touchStartY.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile]);

  if (!toast.visible) return null;

  const icon =
    toast.type === 'success' ? (
      <CheckCircle2 className="text-green-500" size={20} />
    ) : toast.type === 'error' ? (
      <AlertCircle className="text-red-500" size={20} />
    ) : (
      <span className="text-yellow-500 text-lg">🎉</span>
    );

  const textColor =
    toast.type === 'success'
      ? 'text-green-800'
      : toast.type === 'error'
      ? 'text-red-800'
      : 'text-orange-800';

  const glowClass =
    toast.type === 'success'
      ? 'ai-toast-glow'
      : toast.type === 'error'
      ? 'ai-toast-glow'
      : 'ai-toast-glow';

  // truncate for mobile
  const shortMsg =
    isMobile && toast.message.length > 55
      ? toast.message.slice(0, 52).trim() + '…'
      : toast.message;

  // 🟠 Mobile layout
  if (isMobile) {
    return (
      <div
        ref={toastRef}
        role="status"
        aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
        className={`fixed left-4 right-4 bottom-4 ai-safe-bottom z-[2000] ai-toast ai-toast-mobile ${glowClass} ai-animate-in flex items-center gap-2`}
        style={{
          opacity: swipeOpacity,
          padding: '10px 12px',
          transformOrigin: 'bottom center',
        }}
      >
        <div className="icon-wrap scale-90">{icon}</div>
        <div className={`flex-1 text-[13px] font-medium ${textColor}`}>
          {shortMsg}
        </div>
        <button
          onClick={() => setToast(t => ({ ...t, visible: false }))}
          className="ai-close-btn opacity-70 hover:opacity-100"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // 🖥️ Desktop layout
  return (
    <div
      role="status"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={`fixed top-4 right-4 z-[2000] ai-toast ${glowClass} ai-animate-in p-3 flex items-center gap-3`}
    >
      <div className="icon-wrap">{icon}</div>
      <span className={`text-sm font-semibold ${textColor}`}>{toast.message}</span>
      <button
        onClick={() => setToast(t => ({ ...t, visible: false }))}
        className="ai-close-btn ml-1"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default ToastPortal;
